// PhotoPreview.tsx
import React, { Dispatch, SetStateAction, useState, useEffect, useRef } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { CameraIcon, PhotoIcon } from '@heroicons/react/24/outline';
import PhotoSelectionTrustSignals from './photo-selection-trust-signal';

interface PhotoPreviewProps {
  previewUrl: string;
  isMultiFaceError: boolean;
  isMissingFace: boolean;
  setIsMissingFace: Dispatch<SetStateAction<boolean>>;
  onRetake: () => void;
  onUpload: () => void;
  onGenerate: () => void;
  isUploading: boolean;
}

const PhotoPreview: React.FC<PhotoPreviewProps> = ({
     previewUrl,
     isMultiFaceError,
     isMissingFace,
     setIsMissingFace,
     onRetake,
     onUpload,
     onGenerate,
     isUploading
   }) => {
  const [maxImageHeight, setMaxImageHeight] = useState<string | number>('70vh'); // Initial max-height
  const containerRef = useRef<HTMLDivElement>(null); // Ref for the image container
  const buttonContainerRef = useRef<HTMLDivElement>(null); // Ref for button container

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current && buttonContainerRef.current) {
        const containerTop = containerRef.current.offsetTop;
        const buttonContainerHeight = buttonContainerRef.current.offsetHeight;
        const availableHeight = window.innerHeight - containerTop - buttonContainerHeight - 160;

        setMaxImageHeight(availableHeight > 0 ? `${availableHeight}px` : 'auto');
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="max-w-2xl md:mx-auto space-y-4 flex flex-col flex-grow"> {/* Added flex, flex-col, flex-grow */}
      <div
        ref={containerRef}
        className="relative bg-gray-50 rounded-lg overflow-hidden flex items-center justify-center"
        style={{
          height: maxImageHeight,
          minHeight: '160px' // Ensures a minimum height
        }}
      >
        <div className="relative w-full h-full flex items-center justify-center">
          <img
            id="preview-target-face"
            src={previewUrl}
            alt="Selected photo preview"
            className={`w-full h-full ${isMultiFaceError || isMissingFace ? 'opacity-50' : ''}`}
            style={{
              objectFit: 'contain',
              maxWidth: '100%',
              maxHeight: '100%'
            }}
          />
        </div>

        {isMultiFaceError && (
          <div className="absolute inset-0 flex items-center justify-center z-60">
            <div className="bg-white/95 p-4 rounded-lg shadow-lg max-w-sm mx-4">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-gray-900">Multiple Faces Detected</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Please upload a photo with only one face.
                  </p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={onRetake}
                      className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500"
                    >
                      <CameraIcon className="h-4 w-4 mr-2" />
                      Retake Photo
                    </button>
                    <button
                      onClick={onUpload}
                      className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                    >
                      <PhotoIcon className="h-4 w-4 mr-2" />
                      Upload Different
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {isMissingFace && (
          <div className="absolute inset-0 flex items-center justify-center z-60">
            <div className="bg-white/95 p-4 rounded-lg shadow-lg max-w-sm mx-4">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-gray-900">Missing Face Detected</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    If the face is clearly visible please ignore this message. If not please upload a clear photo of
                    your face facing forward.
                  </p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => setIsMissingFace(false)}
                      className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500"
                    >
                      Proceed Anyways
                    </button>
                    <button
                      onClick={onUpload}
                      className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                    >
                      <PhotoIcon className="h-4 w-4 mr-2" />
                      Upload Different
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <PhotoSelectionTrustSignals />
      <div ref={buttonContainerRef} className="flex flex-col sm:flex-row justify-center gap-3">
        {!isMultiFaceError && !isMissingFace && (
          <><button
              onClick={onGenerate}
              className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-500"
              disabled={isUploading}
            >
              Generate Neofaces
            </button>
            <button
              onClick={onRetake}
              className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <CameraIcon className="h-4 w-4 mr-2" />
              Retake
            </button>
            <button
              onClick={onUpload}
              className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <PhotoIcon className="h-4 w-4 mr-2" />
              Upload Different
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PhotoPreview;